import { attributeFormValue } from '../../../components/AttributesInput/AttributesInput';
import {
  createHardwareResponseV,
  Hardware,
  HardwareStatus,
} from '../../domainServices/HardwareService';
import { fetchWrapper } from '../fetchWrapper';

export type AddHardwareFormValues = {
  type?: number;
  category?: number;
  attributes: attributeFormValue[];
  attributeName: number | '';
  attributeValue: string;
  supplier?: number;
  brand?: number;
  organization?: number;
  assigneeType: 'user' | 'office';
  assignee?: number;
  parentHardware: string;
  parentHardwareId: string;
  status: HardwareStatus;
  model: string;
  serialNumbers: { serialNumber: string; jitTag?: string }[];
  warrantyPeriod: Date | null;
  inventoryDate: Date | null;
  purchasePrice?: string | null;
  vatRate?: string | null;
  usableDepreciation?: string | null;
  salesDate: Date | null;
  purchaseDate: Date | null;
  expansibility: boolean;
  comment: string;
  parentDialogOpen: boolean;
  /*
    workaround for
    Some people are setting a new initialValues object and have set enableReinitialize to true,
    but are noticing that on the next render of Formik descendants that
    Formik is providing the prior initialValues and not the new initialValues.
    If another render occurs later and Formik provides the new initialValues,
    it can result in a React warning like Warning: A component is changing an uncontrolled input of type text to be controlled..
    This appears to be a bug in Formik and is solved by setting a key value on Formik that changes every time you set a new initialValues.
    source: https://github.com/jaredpalmer/formik/issues/811#issuecomment-1059814695
  */
  formSourceId: number | null;
};

export const mapHardwareToFormValues: (hardware: Hardware) => AddHardwareFormValues = (
  hardware,
) => ({
  type: hardware.type.id,
  category: hardware.category.id,
  attributeName: '',
  attributeValue: '',
  attributes: hardware.attributes,
  supplier: hardware.supplier.id,
  brand: hardware.brand.id,
  organization: hardware.organization.id,
  assigneeType: 'user',
  assignee: hardware.user ? hardware.user.id : 0,
  parentHardware: '',
  parentHardwareId:
    hardware.parentHardwareId !== 0 ? hardware.parentHardwareId.toString() : '',
  status: hardware.status,
  model: hardware.model,
  serialNumbers: [{ serialNumber: '', jitTag: 'JIT-' }],
  warrantyPeriod: hardware.warrantyPeriod,
  inventoryDate: hardware.inventoryDate,
  purchasePrice: hardware.purchasePrice ? hardware.purchasePrice.toString() : null,
  vatRate: hardware.vatRate ? hardware.vatRate.toString() : null,
  usableDepreciation: hardware.usableDepreciation
    ? hardware.usableDepreciation.toString()
    : null,
  salesDate: hardware.salesDate,
  purchaseDate: hardware.purchaseDate,
  expansibility: hardware.expansibility,
  comment: hardware.comment,
  parentDialogOpen: false,
  formSourceId: hardware.id,
});

export interface AddHardwareDto {
  typeId: number;
  categoryId: number;
  attributes: attributeFormValue[];
  supplierId: number;
  brandId: number;
  organizationId: number;
  userId?: number;
  officeId?: number;
  parentHardwareId?: number;
  status?: HardwareStatus;
  model: string;
  serialNumber?: string;
  jitTag?: string;
  serialNumbers?: { serialNumber: string; jitTag?: string }[];
  warrantyPeriod?: string;
  inventoryDate?: string;
  purchasePrice?: number;
  vatRate?: number;
  usableDepreciation?: string;
  salesDate?: string;
  purchaseDate?: string;
  expansibility?: boolean;
  comment?: string;
}

const jitTagPrefixes = ['JIT-', 'JIT-K'];

export const isBlankJitTag = (jitTag?: string) => {
  const trimmedJitTag = jitTag?.trim() ?? '';
  return trimmedJitTag === '' || jitTagPrefixes.includes(trimmedJitTag);
};

export const mapFormValuesToDto: (values: AddHardwareFormValues) => AddHardwareDto = (
  values,
) => {
  const serialNumbers = values.serialNumbers
    .map(({ serialNumber, jitTag }) => ({
      serialNumber: serialNumber.trim(),
      jitTag: isBlankJitTag(jitTag) ? undefined : jitTag?.trim(),
    }))
    .filter(({ serialNumber }) => serialNumber);
  return {
    typeId: values.type || 0,
    categoryId: values.category || 0,
    attributes: values.attributes,
    supplierId: values.supplier || 0,
    brandId: values.brand || 0,
    organizationId: values.organization || 0,
    officeId: undefined,
    parentHardwareId: Number(values.parentHardwareId) || undefined,
    status: values.status,
    model: values.model,
    // a single row uses the legacy payload, which every backend version accepts
    ...(serialNumbers.length === 1 ? serialNumbers[0] : { serialNumbers }),
    warrantyPeriod: values.warrantyPeriod
      ? values.warrantyPeriod.toISOString()
      : undefined,
    inventoryDate: values.inventoryDate ? values.inventoryDate.toISOString() : undefined,
    purchasePrice: values.purchasePrice ? Number(values.purchasePrice) : undefined,
    vatRate: values.vatRate ? Number(values.vatRate) : undefined,
    usableDepreciation: values.usableDepreciation ? values.usableDepreciation : undefined,
    salesDate: values.salesDate ? values.salesDate.toISOString() : undefined,
    purchaseDate: values.purchaseDate ? values.purchaseDate.toISOString() : undefined,
    expansibility: values.expansibility,
    comment: values.comment,
  };
};

export function addHardware(hardware: AddHardwareFormValues) {
  const hardwareDto = mapFormValuesToDto(hardware);
  return fetchWrapper({
    url: '/api/hardware',
    fetchArg: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: hardwareDto,
    },
    dataType: createHardwareResponseV,
  });
}
